import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';

import { CustomErrorType, MapObjectToHttpParams, PagedResult, waitForIndex } from '@digihaven/shared';

import { DownloadSearch } from '@common/entities/downloads/download-search.entity';
import { ExportJob } from '@common/entities/downloads/export-job.entity';
import { ExportRequest } from '@common/entities/downloads/export-request.entity';
import { ExportEventType } from '@common/entities/downloads/types/export-event.type';
import { HttpService } from '@common/services/http/http.service';
import { ExportTagType, PartialRecord, ShAbstractRecordDownloadService } from '@stylehaven/components';
import { retry, switchMap } from 'rxjs/operators';

@Injectable()
export class DownloadService implements ShAbstractRecordDownloadService {
    private readonly ENDPOINT: string = 'exports';

    constructor(private readonly http: HttpService) {}

    getAll(downloadSearch: DownloadSearch): Observable<PagedResult<ExportJob>> {
        return this.http.get<PagedResult<ExportJob>>(this.ENDPOINT, MapObjectToHttpParams(downloadSearch));
    }

    getOne(exportJobId: string): Observable<ExportJob> {
        return this.http.get<ExportJob>(`${this.ENDPOINT}/${exportJobId}`);
    }

    getFileAsBlob(url: string): Observable<Blob> {
        return this.http.getExternal<Blob>(url, 'blob');
    }

    request(exportRequest: ExportRequest): Observable<ExportJob[]> {
        return this.http.post(this.ENDPOINT, exportRequest);
    }

    getDownloadUrlForAccessRepresentation(record: PartialRecord): Observable<string | null> {
        const representationAsExportSubject: ExportRequest = {
            SubjectId: [`${record.getRecordId()}`],
            Options: {
                Metadata: {
                    Include: false
                }
            },
            EventType: ExportEventType.Consultation,
            Tag: ExportTagType.Consultation
        };

        return this.request(representationAsExportSubject).pipe(
            retry(1),
            switchMap(insertedExportJobs => this.waitForExportJobCompletion(insertedExportJobs))
        );
    }

    private waitForExportJobCompletion(insertedExportJobs: ExportJob[]): Observable<string | null> {
        return this.getOne(insertedExportJobs[0].Id).pipe(
            switchMap(exportJob => {
                const downloadUrl = exportJob.DownloadUrl;

                if (downloadUrl) {
                    return of(downloadUrl);
                } else {
                    return throwError(CustomErrorType.ExportTimeout);
                }
            }),
            waitForIndex()
        );
    }
}
